import {
  interpolateMotionLinkSample,
  type MotionLinkVisualizationData,
  type MotionLinkVisualizationSample,
} from './motionlinkJsonLoader.js';
import type { MotionLinkAnimateSource, MotionLinkPathMode } from './motionlinkRawVsCleaned.js';

export interface MotionLinkVisualizerOptions {
  showPath: boolean;
  showKeyframes: boolean;
  showGrid: boolean;
  showConfidence: boolean;
  pathMode: MotionLinkPathMode;
  animateSource: MotionLinkAnimateSource;
}

const DEFAULT_OPTIONS: MotionLinkVisualizerOptions = {
  showPath: true,
  showKeyframes: true,
  showGrid: true,
  showConfidence: true,
  pathMode: 'cleaned',
  animateSource: 'cleaned',
};

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

function normalizeAngle(value: number): number {
  let angle = value;
  while (angle > Math.PI) angle -= Math.PI * 2;
  while (angle < -Math.PI) angle += Math.PI * 2;
  return angle;
}

export function motionLinkYawToCanvasVector(yawRadians: number, length = 1): { x: number; y: number } {
  const yaw = normalizeAngle(yawRadians);
  return {
    x: Math.sin(yaw) * length,
    y: -Math.cos(yaw) * length,
  };
}

export class MotionLinkVisualizerCanvas {
  private data: MotionLinkVisualizationData | null = null;
  private rawData: MotionLinkVisualizationData | null = null;
  private options: MotionLinkVisualizerOptions = { ...DEFAULT_OPTIONS };
  private currentTimeMs = 0;
  private playing = false;
  private speed = 1;
  private animationFrame = 0;
  private lastAnimationTime = 0;
  private onTimeUpdate?: (timeMs: number) => void;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly context: CanvasRenderingContext2D,
  ) {}

  setData(data: MotionLinkVisualizationData | null, rawData: MotionLinkVisualizationData | null = null): void {
    this.data = data;
    this.rawData = rawData;
    this.currentTimeMs = 0;
    this.pause();
    this.render();
  }

  setOptions(options: Partial<MotionLinkVisualizerOptions>): void {
    this.options = { ...this.options, ...options };
    this.render();
  }

  setPlaybackSpeed(speed: number): void {
    this.speed = clamp(speed, 0.05, 8);
  }

  setTime(timeMs: number): void {
    this.currentTimeMs = clamp(timeMs, 0, this.activeData()?.durationMs ?? 0);
    this.render();
    this.onTimeUpdate?.(this.currentTimeMs);
  }

  getTime(): number {
    return this.currentTimeMs;
  }

  setTimeUpdateHandler(handler: (timeMs: number) => void): void {
    this.onTimeUpdate = handler;
  }

  play(): void {
    if (this.playing || !this.activeData()?.samples.length) return;
    this.playing = true;
    this.lastAnimationTime = 0;
    this.animationFrame = requestAnimationFrame((time) => this.tick(time));
  }

  pause(): void {
    this.playing = false;
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    this.animationFrame = 0;
  }

  restart(): void {
    this.setTime(0);
    this.play();
  }

  destroy(): void {
    this.pause();
    this.onTimeUpdate = undefined;
  }

  render(): void {
    const { width, height } = this.resizeCanvasToDisplay();
    this.context.clearRect(0, 0, width, height);
    this.drawBackground(width, height);

    const activeData = this.activeData();
    if (!activeData?.samples.length) {
      this.drawEmptyState(width, height);
      return;
    }

    const sample = interpolateMotionLinkSample(activeData.samples, this.currentTimeMs) ?? activeData.samples[0]!;
    if (this.options.showGrid) this.drawGrid(width, height);
    if (this.options.showPath) this.drawVisiblePaths(width, height);
    if (this.options.showKeyframes) this.drawKeyframes(width, height, activeData);
    this.drawStartEnd(width, height, activeData);
    this.drawCamera(width, height, sample);
    this.drawReadout(width, height, sample);
  }

  private tick(animationTime: number): void {
    if (!this.playing) return;
    if (!this.lastAnimationTime) this.lastAnimationTime = animationTime;
    const deltaMs = (animationTime - this.lastAnimationTime) * this.speed;
    this.lastAnimationTime = animationTime;
    const duration = this.activeData()?.durationMs ?? 0;
    if (duration <= 0) {
      this.currentTimeMs = 0;
    } else {
      this.currentTimeMs += deltaMs;
      if (this.currentTimeMs >= duration) {
        this.currentTimeMs = duration;
        this.playing = false;
      }
    }
    this.render();
    this.onTimeUpdate?.(this.currentTimeMs);
    if (this.playing) this.animationFrame = requestAnimationFrame((time) => this.tick(time));
  }

  private resizeCanvasToDisplay(): { width: number; height: number } {
    const rect = this.canvas.getBoundingClientRect();
    const width = Math.max(320, Math.round(rect.width || 720));
    const height = Math.max(260, Math.round(rect.height || 420));
    const scale = window.devicePixelRatio || 1;
    const targetWidth = Math.round(width * scale);
    const targetHeight = Math.round(height * scale);
    if (this.canvas.width !== targetWidth || this.canvas.height !== targetHeight) {
      this.canvas.width = targetWidth;
      this.canvas.height = targetHeight;
    }
    this.context.setTransform(scale, 0, 0, scale, 0, 0);
    return { width, height };
  }

  private drawBackground(width: number, height: number): void {
    const gradient = this.context.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, '#101711');
    gradient.addColorStop(0.58, '#070908');
    gradient.addColorStop(1, '#050605');
    this.context.fillStyle = gradient;
    this.context.fillRect(0, 0, width, height);
    this.context.fillStyle = '#dfff5708';
    this.context.beginPath();
    this.context.arc(width * 0.78, height * 0.12, Math.min(width, height) * 0.38, 0, Math.PI * 2);
    this.context.fill();
  }

  private drawEmptyState(width: number, height: number): void {
    this.context.fillStyle = '#8e988e';
    this.context.font = '700 15px Inter, sans-serif';
    this.context.textAlign = 'center';
    this.context.fillText('Load or paste MotionLink JSON to preview motion data', width / 2, height / 2);
    this.context.font = '11px Inter, sans-serif';
    this.context.fillStyle = '#6f796f';
    this.context.fillText('2D visual consistency preview · not a calibrated camera solve', width / 2, height / 2 + 24);
  }

  private drawGrid(width: number, height: number): void {
    const horizon = height * 0.37;
    const floorTop = horizon;
    const floorBottom = height - 34;
    this.context.strokeStyle = '#dfff5718';
    this.context.lineWidth = 1;

    for (let index = -8; index <= 8; index++) {
      const x = width / 2 + index * 38;
      this.context.beginPath();
      this.context.moveTo(width / 2 + index * 4, floorTop);
      this.context.lineTo(x, floorBottom);
      this.context.stroke();
    }

    for (let index = 0; index <= 10; index++) {
      const t = index / 10;
      const y = floorTop + (floorBottom - floorTop) * (t * t);
      const half = 18 + t * width * 0.55;
      this.context.beginPath();
      this.context.moveTo(width / 2 - half, y);
      this.context.lineTo(width / 2 + half, y);
      this.context.stroke();
    }

    this.context.strokeStyle = '#dfff5730';
    this.context.beginPath();
    this.context.moveTo(0, horizon);
    this.context.lineTo(width, horizon);
    this.context.stroke();
  }

  private activeData(): MotionLinkVisualizationData | null {
    if (this.options.animateSource === 'raw' && this.rawData?.samples.length) return this.rawData;
    return this.data;
  }

  private displayBounds(): MotionLinkVisualizationData['bounds'] {
    const visibleData = [
      this.options.pathMode === 'raw' || this.options.pathMode === 'both' ? this.rawData : null,
      this.options.pathMode === 'cleaned' || this.options.pathMode === 'both' ? this.data : null,
    ].filter((item): item is MotionLinkVisualizationData => Boolean(item?.samples.length));
    if (!visibleData.length) return this.activeData()?.bounds ?? { minX: 0, maxX: 0, minZ: 0, maxZ: 0 };
    return {
      minX: Math.min(...visibleData.map((item) => item.bounds.minX)),
      maxX: Math.max(...visibleData.map((item) => item.bounds.maxX)),
      minZ: Math.min(...visibleData.map((item) => item.bounds.minZ)),
      maxZ: Math.max(...visibleData.map((item) => item.bounds.maxZ)),
    };
  }

  private pointFor(sample: MotionLinkVisualizationSample, width: number, height: number): { x: number; y: number } {
    const bounds = this.displayBounds();
    const xSpan = Math.max(0.1, bounds.maxX - bounds.minX);
    const zSpan = Math.max(0.1, bounds.maxZ - bounds.minZ);
    const span = Math.max(xSpan, zSpan, 0.5);
    const normalizedX = (sample.position.x - (bounds.minX + bounds.maxX) / 2) / span;
    const normalizedZ = (sample.position.z - (bounds.minZ + bounds.maxZ) / 2) / span;
    const stageWidth = width * 0.72;
    const stageHeight = height * 0.5;
    return {
      x: width / 2 + normalizedX * stageWidth,
      y: height * 0.68 + normalizedZ * stageHeight,
    };
  }

  private drawVisiblePaths(width: number, height: number): void {
    if ((this.options.pathMode === 'raw' || this.options.pathMode === 'both') && this.rawData?.samples.length) {
      this.drawPath(width, height, this.rawData, {
        color: '#edf8df',
        width: 1.7,
        alpha: 0.34,
        dashed: true,
        arrows: this.options.pathMode === 'raw',
      });
    }
    if ((this.options.pathMode === 'cleaned' || this.options.pathMode === 'both') && this.data?.samples.length) {
      this.drawPath(width, height, this.data, {
        color: '#55dfff',
        width: 3.5,
        alpha: 1,
        dashed: false,
        arrows: true,
      });
    }
  }

  private drawPath(
    width: number,
    height: number,
    data: MotionLinkVisualizationData,
    style: { color: string; width: number; alpha: number; dashed: boolean; arrows: boolean },
  ): void {
    const samples = data.samples;
    if (samples.length < 2) return;

    this.context.lineJoin = 'round';
    this.context.lineCap = 'round';
    this.context.setLineDash([]);
    this.context.beginPath();
    samples.forEach((sample, index) => {
      const point = this.pointFor(sample, width, height);
      if (index === 0) this.context.moveTo(point.x, point.y);
      else this.context.lineTo(point.x, point.y);
    });
    this.context.lineWidth = 8;
    this.context.strokeStyle = `rgba(0, 0, 0, ${0.55 * style.alpha})`;
    this.context.stroke();

    this.context.setLineDash(style.dashed ? [8, 8] : []);
    this.context.beginPath();
    samples.forEach((sample, index) => {
      const point = this.pointFor(sample, width, height);
      if (index === 0) this.context.moveTo(point.x, point.y);
      else this.context.lineTo(point.x, point.y);
    });
    this.context.lineWidth = style.width;
    this.context.shadowColor = style.color;
    this.context.shadowBlur = style.dashed ? 0 : 10;
    this.context.globalAlpha = style.alpha;
    this.context.strokeStyle = style.color;
    this.context.stroke();
    this.context.globalAlpha = 1;
    this.context.shadowBlur = 0;
    this.context.setLineDash([]);

    if (!style.arrows) return;
    this.context.lineWidth = 1;
    this.context.strokeStyle = '#dfff5780';
    for (let index = 2; index < samples.length; index += Math.max(2, Math.floor(samples.length / 9))) {
      const previous = this.pointFor(samples[index - 1]!, width, height);
      const current = this.pointFor(samples[index]!, width, height);
      this.drawArrow(previous.x, previous.y, current.x, current.y, '#dfff57');
    }
  }

  private drawKeyframes(width: number, height: number, data: MotionLinkVisualizationData): void {
    const samples = data.samples;
    const step = Math.max(1, Math.floor(samples.length / 36));
    samples.forEach((sample, index) => {
      if (index % step !== 0 && index !== samples.length - 1) return;
      const point = this.pointFor(sample, width, height);
      const alpha = this.options.showConfidence ? clamp(sample.confidence ?? 0.65, 0.18, 1) : 0.78;
      this.context.fillStyle = `rgba(223, 255, 87, ${alpha})`;
      this.context.beginPath();
      this.context.arc(point.x, point.y, 3.3, 0, Math.PI * 2);
      this.context.fill();
    });
  }

  private drawStartEnd(width: number, height: number, data: MotionLinkVisualizationData): void {
    const samples = data.samples;
    if (!samples.length) return;
    const start = this.pointFor(samples[0]!, width, height);
    const end = this.pointFor(samples[samples.length - 1]!, width, height);
    this.drawMarker(start.x, start.y, '#9eff6d', 'START');
    this.drawMarker(end.x, end.y, '#ff6f91', 'END');
  }

  private drawMarker(x: number, y: number, color: string, label: string): void {
    this.context.fillStyle = color;
    this.context.beginPath();
    this.context.arc(x, y, 7, 0, Math.PI * 2);
    this.context.fill();
    this.context.fillStyle = '#f2f4e8';
    this.context.font = '800 9px Inter, sans-serif';
    this.context.textAlign = 'center';
    this.context.fillText(label, x, y - 12);
  }

  private drawCamera(width: number, height: number, sample: MotionLinkVisualizationSample): void {
    const point = this.pointFor(sample, width, height);
    const yaw = normalizeAngle(sample.rotation?.yaw ?? 0);
    const confidence = this.options.showConfidence ? clamp(sample.confidence ?? 0.75, 0.25, 1) : 1;
    const iconScale = clamp(Math.min(width, height) * 0.045, 12, 20);
    const coneLength = iconScale * 1.7;
    const coneHalfWidth = iconScale * 0.72;
    const bodyWidth = iconScale * 1.28;
    const bodyHeight = iconScale * 0.9;
    const lensWidth = iconScale * 0.5;
    const lensHeight = iconScale * 0.26;

    this.context.save();
    this.context.translate(point.x, point.y);
    this.context.rotate(yaw);

    this.context.fillStyle = `rgba(223, 255, 87, ${0.11 * confidence})`;
    this.context.beginPath();
    this.context.moveTo(0, -coneLength);
    this.context.lineTo(-coneHalfWidth, -iconScale * 0.25);
    this.context.lineTo(coneHalfWidth, -iconScale * 0.25);
    this.context.closePath();
    this.context.fill();

    this.context.fillStyle = `rgba(12, 18, 13, ${0.82})`;
    this.context.strokeStyle = `rgba(223, 255, 87, ${confidence})`;
    this.context.lineWidth = 1.5;
    this.context.beginPath();
    this.context.roundRect(-bodyWidth / 2, -bodyHeight * 0.36, bodyWidth, bodyHeight, 4);
    this.context.fill();
    this.context.stroke();

    this.context.fillStyle = `rgba(85, 223, 255, ${confidence})`;
    this.context.fillRect(-lensWidth / 2, -bodyHeight * 0.66, lensWidth, lensHeight);
    this.context.restore();

    this.context.fillStyle = '#dfff57';
    this.context.beginPath();
    this.context.arc(point.x, point.y, 3.5, 0, Math.PI * 2);
    this.context.fill();

    const forward = motionLinkYawToCanvasVector(yaw, iconScale * 2.25);
    const arrowEnd = { x: point.x + forward.x, y: point.y + forward.y };
    this.drawArrow(point.x, point.y, arrowEnd.x, arrowEnd.y, '#dfff57');
  }

  private drawArrow(x1: number, y1: number, x2: number, y2: number, color: string): void {
    const angle = Math.atan2(y2 - y1, x2 - x1);
    this.context.strokeStyle = color;
    this.context.fillStyle = color;
    this.context.beginPath();
    this.context.moveTo(x1, y1);
    this.context.lineTo(x2, y2);
    this.context.stroke();
    this.context.beginPath();
    this.context.moveTo(x2, y2);
    this.context.lineTo(x2 - Math.cos(angle - 0.5) * 9, y2 - Math.sin(angle - 0.5) * 9);
    this.context.lineTo(x2 - Math.cos(angle + 0.5) * 9, y2 - Math.sin(angle + 0.5) * 9);
    this.context.closePath();
    this.context.fill();
  }

  private drawReadout(width: number, height: number, sample: MotionLinkVisualizationSample): void {
    const pitch = sample.rotation?.pitch ?? 0;
    const yaw = sample.rotation?.yaw ?? 0;
    const roll = sample.rotation?.roll ?? 0;
    const confidence = sample.confidence ?? null;
    this.context.fillStyle = '#07100a99';
    this.context.fillRect(14, 14, 235, 78);
    this.context.strokeStyle = '#dfff5728';
    this.context.strokeRect(14, 14, 235, 78);
    this.context.fillStyle = '#dfff57';
    this.context.font = '800 10px Inter, sans-serif';
    this.context.textAlign = 'left';
    this.context.fillText('VISUAL CONSISTENCY PREVIEW', 26, 34);
    this.context.fillStyle = '#cbd5c6';
    this.context.font = '11px ui-monospace, SFMono-Regular, monospace';
    this.context.fillText(`t ${Math.round(this.currentTimeMs)} ms`, 26, 53);
    this.context.fillText(`yaw ${(yaw * 180 / Math.PI).toFixed(1)}° · pitch ${(pitch * 180 / Math.PI).toFixed(1)}° · roll ${(roll * 180 / Math.PI).toFixed(1)}°`, 26, 70);
    this.context.fillText(`confidence ${confidence === null ? '—' : `${Math.round(confidence * 100)}%`}`, 26, 86);

    this.context.fillStyle = '#6f796f';
    this.context.font = '10px Inter, sans-serif';
    this.context.textAlign = 'right';
    this.context.fillText('2D preview · not SLAM / matchmove / lens solve', width - 18, height - 17);
  }
}
